import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Phone, ShieldCheck, Clock, Award } from "lucide-react";
import Reveal from "@/components/SectionReveal";
import QuoteForm from "@/components/QuoteForm";
import { useSeo, localBusinessJsonLd } from "@/lib/seo";
import { SERVICES, CITIES, SITE } from "@/data/site";
import { SERVICE_DETAILS } from "@/data/serviceDetails";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const detail = SERVICE_DETAILS[slug];

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  useSeo({
    title: service ? `${service.title} in Tampa Bay, FL` : "Pool Service",
    description: service
      ? `${service.title} in Tampa Bay — ${detail?.intro || service.blurb} Serving Clearwater, Tarpon Springs, Trinity, Dunedin, Palm Harbor, Oldsmar, Westchase, Town & Country, Odessa, and Carrollwood.`
      : "",
    path: service ? `/services/${service.slug}` : "/services",
    image: SITE.logo,
    jsonLd: service
      ? localBusinessJsonLd({
          "@type": "Service",
          name: `${service.title} in Tampa Bay`,
          serviceType: service.title,
          provider: { "@type": "LocalBusiness", name: SITE.name },
        })
      : undefined,
  });

  if (!service) return <Navigate to="/services" replace />;

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="pt-32 sm:pt-44 pb-16 bg-gradient-to-br from-sky-50 via-white to-amber-50/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <Link
              to="/services"
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-sky-700"
              data-testid="service-back"
            >
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
          </Reveal>

          <Reveal>
            <p className="mt-6 text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              Tampa Bay Pool Pros
            </p>
            <h1
              className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-4xl"
              data-testid="service-h1"
            >
              {service.title} <span className="text-sky-600">in Tampa Bay, FL</span>
            </h1>
            <p className="mt-5 max-w-2xl text-slate-700 text-lg">{detail?.intro || service.blurb}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#quote"
                data-testid="service-cta-quote"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-slate-900 font-bold hover:bg-amber-300"
              >
                Free quote for {service.title.toLowerCase()}
              </a>
              <a
                href={`tel:${SITE.phoneTel}`}
                data-testid="service-cta-call"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-sky-700"
              >
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-700">
              <li className="inline-flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" /> {SITE.experience} experience
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-sky-600" /> 24/7 emergency
              </li>
              <li className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Licensed &amp; insured
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              What&apos;s included
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              {service.title.toLowerCase()} — done right.
            </h2>
            <p className="mt-4 text-slate-600">{detail?.why}</p>
          </Reveal>

          <Reveal direction="right">
            <ul className="space-y-3">
              {(detail?.includes || []).map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-500 shrink-0" />
                  <span className="text-slate-800">{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Available in cities */}
      <section className="py-12 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 text-center">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-amber-400">
            {service.title} in
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/service-areas/${c.slug}`}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="quote" className="py-16 sm:py-20 bg-gradient-to-b from-sky-50/40 to-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <QuoteForm presetService={service.title} />
        </div>
      </section>
    </main>
  );
}
